import React, { useState, useEffect } from 'react';
import marketingTheme from '../themes/marketingTheme';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Chip,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import 'highlight.js/styles/github-dark.css'; // Dark theme for code highlighting

// Import metadata to get article info
import resourcesMetadata from '../insights/metadata.js';
import { findArticleBySlug } from '../utils/slugUtils';

const Article = () => {
  const { slug } = useParams();
  const [content, setContent] = useState('');
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadArticle = async () => {
      try {
        setLoading(true);
        
        // Find the article in metadata by title-based slug using shared utility
        const foundArticle = findArticleBySlug(resourcesMetadata, slug);

        if (!foundArticle) {
          setError('Article not found');
          setLoading(false);
          return;
        }

        setArticle(foundArticle);

        // Load the markdown content from public directory
        const response = await fetch(`/insights/${foundArticle.content_path}`);
        if (!response.ok) {
          throw new Error(`Failed to load article content: ${response.status} ${response.statusText}`);
        }
        
        const markdownText = await response.text();
        setContent(markdownText);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadArticle();
  }, [slug]);

  if (loading) {
    return (
      <Box 
        sx={{ 
          pt: 10, 
          pb: 8, 
          backgroundColor: '#24242A', 
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <CircularProgress sx={{ color: '#EDB92E' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ pt: 10, pb: 8, backgroundColor: '#24242A', minHeight: '100vh' }}>
        <Container maxWidth="md">
          <Alert severity="error" sx={{ mt: 4 }}>
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 10, pb: 8, backgroundColor: '#24242A', minHeight: '100vh' }}>
      <Container maxWidth="md">
        {/* Article Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '28px', md: '36px' },
              fontWeight: 700,
              color: '#FFFFFF',
              mb: 2,
              lineHeight: 1.2,
            }}
          >
            {article?.title}
          </Typography>
          
          {/* <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {article?.tags?.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                size="small"
                sx={{
                  backgroundColor: '#EDB92E',
                  color: '#000000',
                  fontWeight: 600,
                }}
              />
            ))}
          </Box> */}

          <Typography
            sx={{
              color: '#9CA3AF',
              fontSize: '14px',
              mb: 3,
            }}
          >
            {/* By {article?.author_name} • {new Date(article?.created_at).toLocaleDateString()} */}
            By {article?.author_name}
          </Typography>
        </Box>

        {/* Article Content */}
        <Paper
          sx={{
            backgroundColor: '#2D2D35',
            border: '1px solid #404047',
            p: 4,
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              color: '#FFFFFF',
              fontWeight: 600,
              mb: 2,
            },
            '& h1': { fontSize: '28px' },
            '& h2': { fontSize: '24px' },
            '& h3': { fontSize: '20px' },
            '& p': {
              color: '#E5E7EB',
              lineHeight: 1.7,
              mb: 2,
            },
            '& ul, & ol': {
              color: '#E5E7EB',
              mb: 2,
              pl: 3,
            },
            '& li': {
              mb: 1,
            },
            '& blockquote': {
              borderLeft: `4px solid ${marketingTheme.palette.primary.main}`,
              pl: 3,
              ml: 0,
              fontStyle: 'italic',
              color: '#D1D5DB',
            },
            '& code': {
              backgroundColor: '#1F2937',
              color: '#F3F4F6',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '14px',
            },
            '& pre': {
              backgroundColor: '#1F2937',
              borderRadius: '8px',
              overflow: 'auto',
              mb: 2,
            },
            '& pre code': {
              backgroundColor: 'transparent',
              padding: 0,
            },
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              borderRadius: '8px',
              mb: 2,
            },
            '& a': {
              color: '#EDB92E',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            },
            '& table': {
              width: '100%',
              borderCollapse: 'collapse',
              mb: 2,
            },
            '& th, & td': {
              border: '1px solid #404047',
              padding: '12px',
              textAlign: 'left',
            },
            '& th': {
              backgroundColor: '#1F2937',
              color: '#FFFFFF',
              fontWeight: 600,
            },
            '& td': {
              color: '#E5E7EB',
            },
          }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw, rehypeHighlight]}
          >
            {content}
          </ReactMarkdown>
        </Paper>
      </Container>
    </Box>
  );
};

export default Article;
